import { Injectable } from '@angular/core';
import { BaseApiService } from 'projects/shared/src/lib/core/services/base-api.service';
import { lastValueFrom } from 'rxjs';
import {
  AdjustLongProgramModel,
  AdjustShortProgramsModel,
  AssignLongProgramModel,
  AssignShortProgramsModel,
  ProgramGeneralInfo,
  ProgramsInProgress,
  UserProgram,
  UserProgramsModel,
} from 'projects/shared/src/lib/core/models/userProgram.model';
import { ProgramTask } from '../models/program.model';
import { StudentProgramTaskResult } from '../models/userProgramTaskResult.model';
import { TaskStatsGranularity } from '../enums/taskStatsGranularity.enum';
import { GroupProgramsTasksStats, GroupTaskStatsSortColumn, StudentProgramsTasksStats } from '../models/groupProgramsTasksStats.model';
import { UserProgramsTasksStats } from '../models/userProgramsTasksStats.model';

@Injectable({
  providedIn: 'root',
})
export class UserProgramService extends BaseApiService {
  public async getUserPrograms(): Promise<UserProgram[]> {
    const responseModel = await lastValueFrom(this.get<UserProgramsModel>(`api/v1/user-programs/`));
    return responseModel.userPrograms;
  }

  public async assignShortPrograms(model: AssignShortProgramsModel) {
    return await lastValueFrom(this.post(`api/v1/user-programs/short-programs`, model));
  }

  public async assignLongProgram(model: AssignLongProgramModel) {
    return await lastValueFrom(this.post(`api/v1/user-programs/long-program`, model));
  }

  public async getInProgressPrograms(userId: string): Promise<ProgramsInProgress[]> {
    return await lastValueFrom(this.get(`api/v1/user-programs/get-in-progress-programs?userId=${userId}`));
  }

  public async loadProgramGeneralInfo(userId: string): Promise<ProgramGeneralInfo> {
    return await lastValueFrom(this.get(`api/v1/user-programs/get-program-general-info/${userId}`));
  }

  public async getAvailableProgramTask(): Promise<ProgramTask | null> {
    const responseModel = await lastValueFrom(
      this.get<ProgramTask | null>(`api/v1/user-programs/get-available-task`),
      { defaultValue: null }
    );
    return responseModel;
  }

  public async adjustShortPrograms(model: AdjustShortProgramsModel) {
    return await lastValueFrom(this.post(`api/v1/user-programs/adjust-short-programs`, model));
  }

  public async adjustLongProgram(model: AdjustLongProgramModel) {
    return await lastValueFrom(this.post(`api/v1/user-programs/adjust-long-program`, model));
  }

  public async removeUserProgram(userProgramId: number): Promise<void> {
    return await lastValueFrom(this.delete<void>(`api/v1/user-programs/${userProgramId}`));
  }
  
  public async getUserProgramTaskResult(taskResultId: number): Promise<StudentProgramTaskResult> {
    return await lastValueFrom(this.get<StudentProgramTaskResult>(`api/v1/user-programs/task-results/${taskResultId}`));
  }

  public async getGroupProgramsTasksStats(groupId: number, granularity: TaskStatsGranularity, anchorDate: Date, nextUserId?: string, sortBy?: GroupTaskStatsSortColumn, asc?: boolean): Promise<GroupProgramsTasksStats> {
    const dateOnly = this.getDateOnly(anchorDate);
    let url = `api/v1/user-programs/groups/${groupId}/tasks/stats?granularity=${granularity}&anchorDate=${dateOnly}`
    if (nextUserId) url += `&nextUserId=${nextUserId}`;
    if (sortBy != undefined) url += `&sortBy=${sortBy}`;
    if (asc != null) url += `&asc=${asc}`;

    return await lastValueFrom(this.get<GroupProgramsTasksStats>(url));
  }

  public async getUserProgramsTasksStats(userId: string, granularity: TaskStatsGranularity, anchorDate: Date, pageSize?: number, page?: number): Promise<UserProgramsTasksStats> {
    const dateOnly = this.getDateOnly(anchorDate);
    let url = `api/v1/user-programs/users/${userId}/tasks/stats?granularity=${granularity}&anchorDate=${dateOnly}`
    if (pageSize != undefined) url += `&pageSize=${pageSize}`;
    if (page != undefined) url += `&page=${page}`;

    return await lastValueFrom(this.get<UserProgramsTasksStats>(url));
  }

  private getDateOnly(date: Date) {
    return [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, "0"),
      String(date.getDate()).padStart(2, "0")
    ].join("-");
  }
}
